import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { RegulatoryFilters } from '@/hooks/useRegulatoryQueries'

type FrameworkOption = { slug: string; name: string }

interface Props {
  filters: RegulatoryFilters
  onChange: (next: RegulatoryFilters) => void
  /**
   * Framework options — empty in Stage 1 (Framework dropdown collapses to
   * "All frameworks" only). Stage 2 populates this from the `frameworks`
   * lookup table.
   */
  frameworkOptions?: FrameworkOption[]
}

export function RegulatoryFiltersBar({
  filters,
  onChange,
  frameworkOptions = [],
}: Props) {
  return (
    <div
      role="toolbar"
      aria-label="Regulatory filters"
      data-testid="regulatory-filters-bar"
      className="flex flex-wrap gap-2"
    >
      <Select
        value={filters.jurisdiction}
        onValueChange={(v) =>
          onChange({
            ...filters,
            jurisdiction: v as RegulatoryFilters['jurisdiction'],
          })
        }
      >
        <SelectTrigger className="w-40" aria-label="Jurisdiction">
          <SelectValue placeholder="Jurisdiction" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All jurisdictions</SelectItem>
          <SelectItem value="EU">EU</SelectItem>
          <SelectItem value="US">US</SelectItem>
          <SelectItem value="UK">UK</SelectItem>
          <SelectItem value="International">International</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.framework}
        onValueChange={(v) => onChange({ ...filters, framework: v })}
      >
        <SelectTrigger className="w-48" aria-label="Framework">
          <SelectValue placeholder="Framework" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All frameworks</SelectItem>
          {frameworkOptions.map((f) => (
            <SelectItem key={f.slug} value={f.slug}>
              {f.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.timeframe}
        onValueChange={(v) =>
          onChange({
            ...filters,
            timeframe: v as RegulatoryFilters['timeframe'],
          })
        }
      >
        <SelectTrigger className="w-40" aria-label="Timeframe">
          <SelectValue placeholder="Timeframe" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="90d">Last 90 days</SelectItem>
          <SelectItem value="365d">Last 365 days</SelectItem>
          <SelectItem value="all">All time</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
